import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import { Stack, SxProps, Theme, Typography, useTheme } from "@mui/material";
import { HButton } from "../Button";
import { PulsingIcon } from "../PulsingIcon";
import { WarningIcon } from "./icons";

type TVariants = "warning" | "info" | "altWarning";
export interface IHBanner {
    /** Text to be inserted in the banner */
    text: React.ReactNode;
    /** Function to fire on click of dismiss. Dismiss icon is shown when this is passed */
    onDismiss?: () => void;
    /** Banner variant "warning" or "info" */
    variant?: TVariants;
    sx?: SxProps;
    pulse?: boolean;
    /** Title text which shows in bold, inline with the icon */
    title?: string;
    action?: React.ReactNode;
    stack?: boolean;
}

const BACKGROUND_COLOR = (theme: Theme) => ({
    warning: theme.palette.extendedPink1.main,
    info: theme.palette.mode === "dark" ? theme.palette.extendedGrey2.main : "#EDF5FF",
    altWarning: "#FCF4D6",
});

const TEXT_COLOR = (theme: Theme) => ({
    warning: "unset",
    info: "unset",
    altWarning: theme.palette.common.black,
});

const BORDER_COLOR = (theme: Theme) => ({
    warning: "rgba(218, 30, 40, 0.30)",
    info: theme.palette.mode === "dark" ? BACKGROUND_COLOR(theme).info : "rgba(0, 67, 206, 0.30)",
    altWarning: "rgba(241, 194, 27, 0.30)",
});

const DATA_TESTID = {
    warning: "warning-banner",
    info: "info-banner",
    altWarning: "alt-warning-banner",
};

const ICON_COLOR = (theme: Theme) => ({
    warning: theme.palette.secondaryRed.main,
    info: theme.palette.mode === "dark" ? "#4589FF" : theme.palette.primaryBlue.main, // TODO update this in the color theme
    altWarning: theme.palette.supportWarning.main,
});

const ICON_SECONDARY_COLOR: any = {
    altWarning: "#1a1a1a",
};

const ICON: any = {
    altWarning: WarningIcon,
    warning: WarningIcon,
};

export const HBanner = ({
    text,
    title,
    variant = "warning",
    onDismiss,
    sx,
    pulse = false,
    action,
    stack,
}: IHBanner) => {
    const theme = useTheme();
    const IconComponent = ICON[variant];
    return (
        <Stack
            direction="row"
            spacing={2}
            justifyContent="space-between"
            alignItems="start"
            sx={{
                backgroundColor: BACKGROUND_COLOR(theme)[variant],
                padding: "15px",
                borderColor: BORDER_COLOR(theme)[variant],
                borderStyle: "solid",
                borderWidth: "1px",
                ...sx,
            }}
            data-testid={DATA_TESTID[variant]}
        >
            <Stack direction="row" spacing={2} alignItems="start">
                <PulsingIcon active={pulse} color={ICON_COLOR(theme)[variant]}>
                    <Stack alignItems="start" justifyContent="center" height="100%">
                        {IconComponent ? (
                            <IconComponent
                                color={ICON_COLOR(theme)[variant]}
                                secondaryColor={ICON_SECONDARY_COLOR[variant]}
                            />
                        ) : (
                            <InfoRoundedIcon style={{ color: ICON_COLOR(theme)[variant] }} />
                        )}
                    </Stack>
                </PulsingIcon>
                <Typography color={TEXT_COLOR(theme)[variant]} variant="body2" component="div">
                    {title ? (
                        stack ? (
                            <Stack>
                                <span style={{ fontFamily: "Inter-Bold" }}>{title}</span> {text}
                            </Stack>
                        ) : (
                            <>
                                <span style={{ fontFamily: "Inter-Bold" }}>{title}</span> {text}
                            </>
                        )
                    ) : (
                        text
                    )}
                </Typography>
            </Stack>
            {action}
            {onDismiss && (
                <HButton
                    variant="text"
                    size="small"
                    onClick={onDismiss}
                    sx={{
                        height: "24px",
                        width: "24px",
                        color: TEXT_COLOR(theme)[variant],
                    }}
                >
                    <CloseRoundedIcon fontSize="small" />
                </HButton>
            )}
        </Stack>
    );
};
